import React, { useContext } from 'react';
import Swiper from 'react-id-swiper';
import LazyLoad from 'react-lazyload';
import MediaPreloader from '@com/core/common/components/mediaPreloader';
import ModuleHeader from '@com/core/common/components/static/moduleHeader/moduleHeader';
import BannerTimer from '@com/core/common/components/static/bannerTimer/bannerTimer';
import isEmpty from 'lodash/isEmpty';
import GlobalGalleryStyle from '@com/core/common/styles/GlobaleImageGallery';
import styled from 'styled-components';
import { colors } from '@com/core/kul/styles/kulTheme';
import { TBannerSlider, TBannerSliderBanner } from '@com/core/common/types/catalog';
import Link from '@com/core/common/components/Link';
import { AppContext } from '../../../../common/contexts/appContext';

type TBannerSliderProps = {
    bgColor?: string;
    iconColor?: string;
};

const BannerSliderContainer = styled.div<TBannerSliderProps>`
    /* SWIPER OVERIDES */
    .swiper-pagination {
        bottom: 12px;
    }

    .swiper-pagination > div {
        display: inline-flex;
        padding: 3px;
        border-radius: 20px;
        background-color: ${colors.bgPrimary};
    }

    .swiper-pagination span {
        width: 5px;
        height: 5px;
        margin: 0 3px;
        border-radius: 100%;
        border: solid 2px ${colors.body};
    }

    .swiper-pagination span.active {
        background-color: ${colors.brandPrimary};
        border: solid 2px ${colors.brandPrimary};
    }
`;

const HeaderContainer = styled.div`
    padding: 28px 0 0;
`;

const Slide = styled.div`
    & > a {
        display: block;
    }
`;

const BannerWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: -15px;
`;

const TimerWrapper = styled.div`
    position: absolute;
    pointer-events: none;
`;

const TimerContainer = styled.div<{ color?: string }>`
    background-size: cover;
    color: ${({ color }: { color?: string }): string => color || colors.bodyAlt};
    font-weight: bold;
    padding: 5px;
`;

type TProps = {
    moduleData: TBannerSlider;
    iconColor?: string;
};

const BannerSlider = ({ iconColor, moduleData }: TProps): JSX.Element => {
    const { banners, bgColor, moduleHeader, textColor } = moduleData;

    const { locale } = useContext(AppContext);

    if (isEmpty(banners)) {
        return <div />;
    }

    const getSlideContent = (banner: TBannerSliderBanner): JSX.Element => {
        let slide = (
            <div>
                <img src={banner.imageUrl} alt={banner.linkUrl} />
                {banner.timer && (
                    <TimerWrapper
                        style={
                            banner.timer.coordinates
                                ? {
                                      left: `${banner.timer.coordinates.x}`,
                                      top: `${banner.timer.coordinates.y}`,
                                      transform: `translate(-${banner.timer.coordinates.x}, -${banner.timer.coordinates.y})`,
                                  }
                                : {}
                        }>
                        <div
                            className="timerContainer"
                            style={{
                                background: banner.timer.imageUrl
                                    ? `url(${banner.timer.imageUrl}) no-repeat 0 0`
                                    : 'transparent',
                                ...(banner.timer.bgColor && { backgroundColor: banner.timer.bgColor }),
                                color: `${banner.timer.fontColor ? banner.timer.fontColor : '#000'}`,
                            }}>
                            <BannerTimer
                                endTime={banner.timer.expiry}
                                disableDays={banner.timer.disableDays ? banner.timer.disableDays : false}
                                displayUnitsBottom={
                                    banner.timer.displayUnitsBottom ? banner.timer.displayUnitsBottom : false
                                }
                                isSlider
                                timerFontSize={banner.timer.fontSize ? banner.timer.fontSize : ''}
                            />
                        </div>
                    </TimerWrapper>
                )}
            </div>
        );

        if (banner.videoUrl || (banner.imageUrl && /\w*.mp4$/.test(banner.imageUrl))) {
            slide = (
                <BannerWrapper>
                    <video loop autoPlay>
                        <track default kind="captions" />
                        <source src={banner.videoUrl || banner.imageUrl} type="video/mp4" />
                    </video>
                    {banner.timer && (
                        <TimerWrapper
                            style={
                                banner.timer.coordinates
                                    ? {
                                          left: `${banner.timer.coordinates.x}`,
                                          top: `${banner.timer.coordinates.y}`,
                                          transform: `translate(-${banner.timer.coordinates.x}, -${banner.timer.coordinates.y})`,
                                      }
                                    : {}
                            }>
                            <TimerContainer
                                style={{
                                    background: banner.timer.imageUrl
                                        ? `url(${banner.timer.imageUrl}) no-repeat 0 0`
                                        : 'transparent',
                                    ...(banner.timer.bgColor && { backgroundColor: banner.timer.bgColor }),
                                    color: `${banner.timer.fontColor ? banner.timer.fontColor : '#000'}`,
                                }}>
                                <BannerTimer
                                    endTime={banner.timer.expiry}
                                    disableDays={banner.timer.disableDays ? banner.timer.disableDays : false}
                                    displayUnitsBottom={
                                        banner.timer.displayUnitsBottom ? banner.timer.displayUnitsBottom : false
                                    }
                                    isSlider
                                    timerFontSize={banner.timer.fontSize ? banner.timer.fontSize : ''}
                                />
                            </TimerContainer>
                        </TimerWrapper>
                    )}
                </BannerWrapper>
            );
        }

        return (
            <Link
                href={banner.linkUrl}
                analytics={{
                    platform: 'web',
                    ecommerce: {
                        promoClick: {
                            promotions: [
                                {
                                    id: banner.gapt.name,
                                    name: banner.gapt.name,
                                    position: banner.gapt.position,
                                },
                            ],
                        },
                    },
                }}>
                <MediaPreloader ratio={banner.ratio}>{slide}</MediaPreloader>
            </Link>
        );
    };
    const firstBanner = banners && banners[0];
    const placeholder = firstBanner ? <Slide>{getSlideContent(firstBanner)}</Slide> : <div />;

    const params = {
        shouldSwiperUpdate: true,
        loop: true,
        autoplay: {
            delay: 3000,
        },
        pagination: {
            type: 'custom',
            el: '.swiper-pagination',
            renderCustom: (_: never, current: number, total: number): string => {
                let bullets = '';
                for (let i = 0; i < total; i += 1) {
                    bullets += `<span class="${current === i + 1 ? 'active' : ''}"></span>`;
                }
                return `<div>${bullets}</div>`;
            },
        },
        navigation: {
            nextEl: '.swiper-control-next',
            prevEl: '.swiper-control-prev',
        },
    };

    return (
        <BannerSliderContainer bgColor={bgColor} iconColor={iconColor}>
            <GlobalGalleryStyle />
            {/* MODULE HEADER START */}
            {moduleHeader && (
                <HeaderContainer>
                    <ModuleHeader className="siteWidthContainer" data={moduleHeader} textColor={textColor} />
                </HeaderContainer>
            )}
            <LazyLoad placeholder={placeholder} once offset={300}>
                <GlobalGalleryStyle />
                {/* Disabled because Swiper props don't type param types */}
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <Swiper {...params}>
                    {banners.map(banner => (
                        <Slide key={banner.imageUrl}>{getSlideContent(banner)}</Slide>
                    ))}
                </Swiper>
            </LazyLoad>
        </BannerSliderContainer>
    );
};

export default BannerSlider;
